// src/Pages/DealerDetails.jsx
import React, { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Template from "../Components/Template";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useGetDealerByIdQuery } from "../Services/modules/dealer";
import { Config } from "../Config";
import { api } from "../Services/api";

// ---- UI helpers (layout only; no business logic changed) ----
const CardSection = ({ title, children }) => (
  <section className="mb-4">
    <div className="card shadow-sm">
      <div className="card-header bg-white">
        <h6 className="m-0">{title}</h6>
      </div>
      <div className="card-body">{children}</div>
    </div>
  </section>
);

const FieldTile = ({ label, value }) => (
  <div className="h-100 border rounded-3 p-3 bg-white">
    <div className="text-muted small mb-1">{label}</div>
    <div className="fw-semibold">{value || <span className="text-muted">—</span>}</div>
  </div>
);

const isImageUrl = (url = "") =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test((url.split("?")[0] || "").toLowerCase());

// Simple dependency-free lightbox
const ImageLightbox = ({ open, src, title, onClose }) => {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="position-fixed top-0 start-0 w-100 h-100"
      style={{ background: "rgba(0,0,0,.75)", zIndex: 1050 }}
      onClick={onClose}
    >
      <div
        className="position-absolute top-50 start-50 translate-middle p-2"
        style={{ maxWidth: "90vw", maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white rounded-3 shadow" style={{ overflow: "hidden" }}>
          <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
            <div className="fw-semibold small">{title || "Preview"}</div>
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>
              Close
            </button>
          </div>
          <div className="p-2" style={{ background: "#111" }}>
            <img
              src={src}
              alt={title || "Document preview"}
              style={{
                maxWidth: "86vw",
                maxHeight: "78vh",
                display: "block",
                margin: "0 auto",
                objectFit: "contain",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

function DealerDetails() {
  const navigate = useNavigate();
  const location = useLocation();

  // read ?dealerId=...
  const queryParams = new URLSearchParams(location.search);
  const dealerId = queryParams.get("dealerId");

  const { data, isLoading, isError, error, refetch } = useGetDealerByIdQuery(
    { dealerId: dealerId },
    { skip: !dealerId }
  );

  const dealer = useMemo(() => data?.data || null, [data]);

  // lightbox state
  const [lightbox, setLightbox] = useState({ open: false, src: "", title: "" });
  const openLightbox = (src, title) => setLightbox({ open: true, src, title });
  const closeLightbox = () => setLightbox({ open: false, src: "", title: "" });

 const downloadPdf = async (ref) => {
  try {
   // const token = store.getState()?.userData?.user_data?.token;

    const resp = await fetch(`${Config.API_URL_LOCAL}/dealer/createDealerRegistrationPdf`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
       // ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ refNumber: ref }),
    });

    // Non-200? try to parse JSON error
    if (!resp.ok) {
      const maybeJson = await resp.text();
      try {
        const j = JSON.parse(maybeJson);
        throw new Error(j.message || "Failed to generate PDF");
      } catch {
        throw new Error("Failed to generate PDF");
      }
    }

    const contentType = resp.headers.get("content-type") || "";
    if (!contentType.includes("application/pdf")) {
      const t = await resp.text();
      try {
        const j = JSON.parse(t);
        throw new Error(j.message || "Unexpected response while generating PDF");
      } catch {
        throw new Error("Unexpected non-PDF response from server");
      }
    }

    const blob = await resp.blob();
    const cd = resp.headers.get("content-disposition") || "";
    const match = cd.match(/filename="?(.+?)"?$/);
    const filename = match?.[1] || `Dealer_${ref}.pdf`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    return { error: false };
  } catch (e) {
    return { error: true, message: e?.message || "Failed to generate PDF" };
  }
};

  // Files renderer: thumbnails for images, buttons for others
  const renderFiles = (arr, title,folderKey) => {
    if (!Array.isArray(arr) || arr.length === 0) return null;

    return (
      <div className="mb-2">
        <div className="mb-2 fw-semibold">{title}</div>
        <div className="row g-3">
          {arr.map((f, idx) => {
           // const url = f?.filePath || "";
          const url = `${Config.API_URL_IMAGE}/${folderKey}/${f.filePath}`;

            const label = f?.imgType ? `${f.imgType}-${idx + 1}` : `file-${idx + 1}`;

            if (isImageUrl(url)) {
              return (
                <div key={`${title}-${idx}`} className="col-6 col-md-3 col-lg-2">
                  <div
                    className="border rounded-3 overflow-hidden bg-white"
                    style={{ cursor: "pointer" }}
                    onClick={() => openLightbox(url, label)}
                  >
                    <img
                      src={url}
                      alt={label}
                      className="w-100"
                      style={{ display: "block", height: 120, objectFit: "cover" }}
                    />
                    <div className="px-2 py-1 small text-truncate border-top bg-light">{label}</div>
                  </div>
                </div>
              );
            }

            // non-image (e.g., PDF)
            return (
              <div key={`${title}-${idx}`} className="col-12 col-sm-6 col-md-4 col-lg-3">
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-sm btn-outline-secondary w-100 text-start"
                  title={label}
                  style={{ wordBreak: "break-all" }}
                >
                  <span className="me-2">📄</span>
                  {label}
                </a>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Template>
      <ToastContainer />
      <main id="main" className="main">
        <div className="container">
          {/* Header bar */}
          <div className="d-flex align-items-center justify-content-between mb-4">
            <div>
              <h4 className="m-0">Dealer Details</h4>
              <div className="text-muted small">
                Reference No: <span className="fw-semibold">{dealerId}</span>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                Back
              </button>
              {/* <button className="btn btn-outline-primary" onClick={() => refetch()}>
                Refresh
              </button> */}
              {/* <button className="btn btn-primary" disabled>
                Download PDF
              </button> */}
               <button
                  onClick={() => downloadPdf(dealerId)}
                  className="btn btn-primary"
                  disabled={!dealerId}
                  title={!dealerId ? "Missing reference number" : "Download PDF"}
                >
                  Download PDF
                </button>
            </div>
          </div>

          {/* Loading / Error / Empty */}
          {isLoading && (
            <div className="card shadow-sm mb-4">
              <div className="card-body text-muted">Loading dealer details…</div>
            </div>
          )}

          {isError && (
            <div className="card shadow-sm mb-4">
              <div className="card-body">
                <div className="alert alert-danger mb-0">
                  {error?.data?.message || "Failed to fetch dealer"}
                </div>
              </div>
            </div>
          )}

          {!isLoading && !dealer && !isError && (
            <div className="card shadow-sm mb-4">
              <div className="card-body text-muted">No dealer found for ref {dealerId}.</div>
            </div>
          )}

          {/* CONTENT */}
          {dealer && (
            <>
              {/* BASIC */}
              <CardSection title="Basic">
                <div className="row g-3">
                  <div className="col-md-6">
                    <FieldTile label="Name" value={dealer.dealerName} />
                  </div>
                  <div className="col-md-6">
                    <FieldTile label="Shop Name" value={dealer.dealerShopName} />
                  </div>
                  <div className="col-md-4">
                    <FieldTile label="WhatsApp" value={dealer.whatsappNumber} />
                  </div>
                  <div className="col-md-4">
                    <FieldTile label="Alternate Contact" value={dealer.alternateContactNumber} />
                  </div>
                  <div className="col-md-4">
                    <FieldTile label="Email" value={dealer.email} />
                  </div>
                </div>
              </CardSection>

              {/* ADDRESS */}
              <CardSection title="Address">
                <div className="row g-3">
                  <div className="col-12">
                    <FieldTile label="Permanent Address" value={dealer.permanentAddress} />
                  </div>
                  <div className="col-md-3">
                    <FieldTile label="At / Post" value={dealer.addressAtPost} />
                  </div>
                  <div className="col-md-3">
                    <FieldTile label="Taluka" value={dealer.addressTaluka} />
                  </div>
                  <div className="col-md-3">
                    <FieldTile label="District" value={dealer.addressDistrict} />
                  </div>
                  <div className="col-md-3">
                    <FieldTile label="State" value={dealer.addressState} />
                  </div>
                  <div className="col-md-3">
                    <FieldTile label="PIN" value={dealer.addressPin} />
                  </div>
                </div>
              </CardSection>

              {/* GODOWN (separate as requested) */}
              <CardSection title="Godown">
                <div className="row g-3">
                  <div className="col-md-8">
                    <FieldTile label="Godown Address" value={dealer.godownAddress} />
                  </div>
                  <div className="col-md-4">
                    <FieldTile label="Capacity (MT)" value={dealer.godownCapacity} />
                  </div>
                </div>
              </CardSection>

              {/* CROPS (separate as requested) */}
              <CardSection title="Crops">
                <div className="row g-3">
                  <div className="col-md-6">
                    <FieldTile label="Major Crops" value={dealer.majorCrops} />
                  </div>
                  <div className="col-md-6">
                    <FieldTile label="Irrigation Status" value={dealer.irrigationStatus} />
                  </div>
                </div>
              </CardSection>

              {/* GST / PAN / BANK */}
              <CardSection title="GST / PAN / Bank">
                <div className="row g-3">
                  <div className="col-md-4">
                    <FieldTile label="GST No." value={dealer.gstNo} />
                  </div>
                  <div className="col-md-4">
                    <FieldTile label="PAN" value={dealer.panNo} />
                  </div>
                  <div className="col-md-4">
                    <FieldTile label="Bank" value={dealer.bankName} />
                  </div>
                  <div className="col-md-4">
                    <FieldTile label="Branch" value={dealer.bankBranch} />
                  </div>
                  <div className="col-md-4">
                    <FieldTile label="Account No." value={dealer.bankAccountNo} />
                  </div>
                  <div className="col-md-4">
                    <FieldTile label="IFSC" value={dealer.bankIfsc} />
                  </div>
                </div>
              </CardSection>

              {/* CONSENT (if present) */}
              {dealer.consent && (
                <CardSection title="Consent">
                  <div className="row g-3">
                    <div className="col-md-3">
                      <FieldTile label="Accepted" value={dealer.consent.accepted ? "Yes" : "No"} />
                    </div>
                    <div className="col-md-3">
                      <FieldTile label="Terms Version" value={dealer.consent.terms_version} />
                    </div>
                    <div className="col-md-3">
                      <FieldTile label="Accepted At" value={dealer.consent.accepted_at_iso} />
                    </div>
                    <div className="col-md-3">
                      <FieldTile label="IP" value={dealer.consent.ip_address} />
                    </div>
                  </div>
                </CardSection>
              )}

              {/* DOCUMENTS */}
              <CardSection title="Documents">
                {renderFiles(dealer.dealerPhoto, "Dealer Photo","dealerPhoto")}
                {renderFiles(dealer.blankChequePhoto, "Blank Cheque","blankChequePhoto")}
                {renderFiles(dealer.aadharCardPhoto, "Aadhar Card","aadharCardPhoto")}
                {renderFiles(dealer.pesticideLicensePhoto, "Pesticide License","pesticideLicensePhoto")}
                {renderFiles(dealer.fertiliserLicensePhoto, "Fertiliser License","fertiliserLicensePhoto")}
                {renderFiles(dealer.selfDeclarationPhoto, "Self Declaration","selfDeclarationPhoto")}
              </CardSection>

              {/* META */}
              <CardSection title="Meta">
                <div className="row g-3">
                  <div className="col-md-3">
                    <FieldTile label="Created At" value={dealer.created_date} />
                  </div>
                  <div className="col-md-3">
                    <FieldTile label="Created Time" value={dealer.created_time} />
                  </div>
                </div>
              </CardSection>
            </>
          )}
        </div>
      </main>

      {/* Lightbox for images */}
      <ImageLightbox
        open={lightbox.open}
        src={lightbox.src}
        title={lightbox.title}
        onClose={closeLightbox}
      />
    </Template>
  );
}

export default DealerDetails;
